import { useSyncExternalStore } from "react";
import type { FailedReceipt, PrinterStatus } from "./hardware";

/**
 * What the POS knows about its own printer, shared by the header pill, the "receipt did not print" banner and the heartbeat.
 * Fed by the Electron bridge (status events + a slow poll as a safety net); stays empty in a plain browser.
 */
export interface HardwareSnapshot {
  native: boolean;
  status: PrinterStatus | null;
  failed: FailedReceipt[];
  connection: string | null;
  paperWidth: number | null;
  version: string | null;
  scannerOn: boolean;
  updateAvailable: boolean;
}

let snap: HardwareSnapshot = { native: false, status: null, failed: [], connection: null, paperWidth: null, version: null, scannerOn: false, updateAvailable: false };
const listeners = new Set<() => void>();
const set = (patch: Partial<HardwareSnapshot>) => {
  snap = { ...snap, ...patch };
  listeners.forEach((l) => l());
};

export const getHardwareSnapshot = () => snap;
export const setUpdateAvailable = (v: boolean) => { if (snap.updateAvailable !== v) set({ updateAvailable: v }); };
export const useHardwareSnapshot = (): HardwareSnapshot => useSyncExternalStore((cb) => { listeners.add(cb); return () => void listeners.delete(cb); }, getHardwareSnapshot);

export async function refreshHardware(): Promise<void> {
  const api = window.hardware?.settings;
  if (!api) return;
  try {
    const [status, failed, cfg, info] = await Promise.all([api.status(), api.failedJobs(), api.get(), snap.version ? Promise.resolve(null) : api.appInfo()]);
    set({ native: true, status, failed, connection: cfg.printer.connection.type, paperWidth: cfg.printer.paperWidth, scannerOn: cfg.scanner.serial.enabled, ...(info ? { version: info.version } : {}) });
  } catch {
    /* an older terminal without the hardware screen: keep what we have */
  }
}

/** Starts listening; returns a stop function. */
export function startHardwareMonitor(): () => void {
  const api = window.hardware?.settings;
  if (!api) return () => undefined;
  set({ native: true });
  void refreshHardware();
  const off = api.onStatus((s) => { set({ status: s }); void refreshHardware(); });
  const timer = setInterval(() => void refreshHardware(), 60_000);
  return () => { off(); clearInterval(timer); };
}
